export type ClientRow = {
  id: string;
  company_name: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  status: string;
  notes: string | null;
  user_id: string | null;
  created_at: string;
  preferred_language?: "es" | "en";
  /** Default payment method that will pre-fill new invoices for this client. */
  default_payment_method_id?: string | null;
  /** IANA timezone (e.g. "America/Mexico_City"). Used to anchor meeting scheduling. */
  timezone?: string | null;
  /** True when this is a demo / presentation-only client. Hidden globally
   *  when the "Clientes simulados" filter is OFF. */
  is_simulated?: boolean;
  /** "special" clients are private — visible only to jmartinez@smart-canvas.com. */
  client_type?: "standard" | "special";
  projects_count?: number;
  billed_total?: number;
  outstanding?: number;
  last_activity?: string | null;
};

export type ProjectRow = {
  id: string;
  name: string;
  description: string | null;
  client_id: string | null;
  status: string;
  progress: number;
  budget: number | null;
  currency: string;
  start_date?: string | null;
  due_date?: string | null;
  created_at: string;
  clients?: { company_name: string } | null;
  team_count?: number;
  open_tasks?: number;
  milestones_done?: number;
  milestones_total?: number;
  last_update?: string | null;
  /** Lightweight team avatar bundle for the Cards view (max 6 sent over the wire). */
  team_members?: Array<{
    id: string;
    display_name: string;
    avatar_url: string | null;
    role_on_project?: string | null;
  }>;
  /**
   * Snapshot of the global resources discount at the moment this project
   * was created. Locks in the price guarantee made to the client and is
   * used for any NEW team assignment on this project. Existing memberships
   * keep their own `monthly_rate_snapshot` for full traceability.
   */
  resources_discount_percent_snapshot?: number | null;
  /** Per-project billing overrides (monthly fee, invoice day, due day). */
  billing?: {
    monthly_fee?: number | null;
    invoice_day_of_month?: number | null;
    payment_due_day?: number | null;
    currency?: string | null;
  } | null;
  /**
   * Per-project AI Simulations settings (kill switch, tone, schedule,
   * budget, etc.). Replaces the legacy global setting that lived under
   * app_settings.project_tab_settings.ai_simulations. Admin-only UI.
   */
  ai_settings?: AISimulationsSettings | null;
};

export type Milestone = {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  status: string;
  order_index: number;
  start_date?: string | null;
  end_date?: string | null;
  due_date?: string | null;
  created_at: string;
};

export type Task = {
  id: string;
  project_id: string;
  milestone_id?: string | null;
  title: string;
  description: string | null;
  status: string;
  assignee_id?: string | null;
  due_date?: string | null;
  visible_to_client?: boolean;
  created_at: string;
};

export type ProjectUpdate = {
  id: string;
  project_id: string;
  author_id?: string | null;
  title: string;
  body: string | null;
  visible_to_client?: boolean;
  created_at: string;
};

export type ProjectMember = {
  user_id: string;
  project_id: string;
  role: string;
  created_at?: string;
  profiles?: { full_name: string | null; email: string | null } | null;
};

export type Invoice = {
  id: string;
  client_id: string | null;
  invoice_number: string | null;
  amount: number;
  currency: string;
  status: string;
  issue_date: string | null;
  due_date: string | null;
  paid_at: string | null;
  created_at: string;
  clients?: { company_name: string } | null;
  notes?: string | null;
  /** Suggested payment method (resolved from client default or global default). */
  payment_method_id?: string | null;
  /** Optional PDF/PNG attached to the issued invoice (uploaded copy of the official document). */
  attachment_url?: string | null;
  attachment_path?: string | null;
  attachment_name?: string | null;
  attachment_mime?: string | null;
  /** Reminder dispatch cadence after due date when balance > 0. */
  reminder_cadence?: "off" | "daily" | "weekly" | "monthly" | null;
  last_reminder_at?: string | null;
};

export type InvoiceReminder = {
  id: string;
  invoice_id: string;
  sent_at: string;
  kind: "automatic" | "manual";
  cadence: string | null;
  outstanding_amount: number;
  currency: string;
  channel: string | null;
  notes: string | null;
  created_at: string;
};

export type Quote = {
  id: string;
  title: string;
  quote_number?: string | null;
  client_id: string | null;
  prospect_name: string | null;
  prospect_email: string | null;
  status: string;
  /** When true, treat as demo/simulated data (hidden when "Simulated clients" is off). */
  is_simulated?: boolean;
  currency: string;
  notes: string | null;
  valid_until: string | null;
  line_items: any;
  subtotal: number;
  tax: number;
  total: number;
  created_at: string;
  last_opened_at?: string | null;
  notifications_sent?: string[] | null;
  clients?: {
    company_name: string;
    contact_name?: string | null;
    contact_email?: string | null;
    user_id?: string | null;
    status?: string | null;
  } | null;
  // Extended fields (AI wizard / detail page)
  description?: string | null;
  capabilities?: string[];
  ai_payload?: any | null;
  selected_budget?: "conservative" | "balanced" | "optimal";
  invoice_day_of_month?: number | null;
  payment_due_day?: number | null;
  template?: string;
  monthly_fee?: number | null;
  annual_fee?: number | null;
  /**
   * Optional per-quote discount. When present, the quote header / numbers
   * card surfaces the original price, the savings and the expiration date
   * before which the proposal must be signed for the discount to apply.
   * NOTE: still mock-only — edited from the Quote Wizard in a follow-up.
   */
  discount?: {
    percent: number;          // 0–100
    reason: string;           // why we are giving this discount
    reason_en?: string;       // optional English copy (preferred when lang=en)
    reason_es?: string;       // optional Spanish copy (preferred when lang=es)
    expires_at: string;       // ISO date — sign-by deadline
  } | null;
  /**
   * Discount snapshot captured the first time the quote leaves draft state.
   * Used to render the quote with stable totals regardless of later changes
   * to the global resources discount.
   */
  discount_snapshot?: {
    percent: number;
    applied_at: string;   // ISO timestamp
  } | null;
  /** Project created from this quote, if any. Prevents reusing the quote. */
  project_id?: string | null;
};

export type QuoteResourceRow = {
  id: string;
  quote_id: string;
  resource_id: string | null;
  role_label: string | null;
  quantity: number;
  unit_price_override: number | null;
  justification: string | null;
  order_index: number;
};

export type QuoteTeamRow = {
  id: string;
  quote_id: string;
  team_profile_id: string;
  role_on_project: string | null;
  match_reason: string | null;
  order_index: number;
  team_profiles?: {
    id: string;
    display_name: string;
    headline: string | null;
    avatar_url: string | null;
    expertise?: string[];
  } | null;
};

export type Payment = {
  id: string;
  invoice_id: string | null;
  amount: number;
  currency: string;
  method: string | null;
  reference?: string | null;
  notes?: string | null;
  paid_at: string;
  invoices?: { invoice_number: string | null; clients?: { company_name: string } | null } | null;
  /** Optional PDF/PNG of the payment receipt / proof. */
  receipt_url?: string | null;
  receipt_path?: string | null;
  receipt_name?: string | null;
  receipt_mime?: string | null;
  /** Lifecycle of the payment record. Declared payments are submitted by clients and need Finance to confirm. */
  status?: "declared" | "accepted" | "rejected";
  /** User id of the client who declared the payment (only set for status="declared"). */
  declared_by?: string | null;
  /** Optional display name of the declarer — convenience for mock/seed data. */
  declared_by_name?: string | null;
};

export type UserWithRoles = {
  id: string;
  email: string | null;
  full_name: string | null;
  roles: string[];
  company?: string | null;
  avatar_url?: string | null;
  position?: string | null;
  country?: string | null;
  created_at?: string | null;
  /** Built-in/protected user — cannot be deleted nor stripped of its primary role. */
  is_protected?: boolean;
  /** Internal division key (matches `os.resources.divisions.*` translations). */
  division?: string | null;
  /** Specialty / job headline (e.g. "Senior Frontend Engineer"). */
  specialty?: string | null;
  /** Spanish translation of specialty (preferred when UI locale is es). */
  specialty_es?: string | null;
  /** Linked team profile id (operators only). */
  profile_id?: string | null;
  /** True when this user came from a project_invitations row (client-side
   *  collaborator invited inside a project). Surfaces as a "Client invitee"
   *  badge in the Users page. */
  is_project_invitee?: boolean;
};

export type DashboardStats = {
  clients: number;
  activeProjects: number;
  openInvoices: number;
  overdue: number;
  revenue: number;
};

export type AdminAlert = {
  id: string;
  /** Maps to a sidebar section so the UI can group / badge. */
  section: "invoices" | "projects" | "quotes" | "clients";
  severity: "critical" | "warning" | "info";
  /** i18n key under `os.adminDashboard.alerts.kinds` (e.g. "invoice_overdue") */
  kind: string;
  title: string;
  subtitle?: string;
  href: string;
  amount?: number;
  currency?: string;
  date?: string | null;
};

export type AlertCounts = {
  invoices: number;
  projects: number;
  quotes: number;
  clients: number;
  total: number;
};

export type AdminInsights = {
  stats: DashboardStats & { quotesOpen: number; outstanding: number };
  pipeline: {
    clientsByStatus: { status: string; count: number }[];
    projectsByStatus: { status: string; count: number }[];
  };
  /** Last 6 months revenue (paid invoices), oldest first. */
  revenueByMonth: { month: string; total: number }[];
  topOutstanding: { client_id: string; company_name: string; outstanding: number }[];
  recentActivity: {
    id: string;
    kind: "invoice_paid" | "project_update" | "quote_sent" | "client_new";
    title: string;
    subtitle?: string;
    at: string;
    href: string;
  }[];
  alerts: AdminAlert[];
  alertCounts: AlertCounts;
};

export type OperatorAssignee = {
  user_id: string;
  profiles?: { full_name: string | null; email: string | null } | null;
};

import type { I18nText } from "./i18nText";

export type ExperienceItem = {
  role: I18nText;
  company: I18nText;
  period?: I18nText;
  description?: I18nText;
};

export type EducationItem = {
  degree: I18nText;
  school: I18nText;
  year?: I18nText;
};

export type TeamProfile = {
  id: string;
  user_id: string | null;
  display_name: string;
  headline: string | null;
  bio: I18nText;
  avatar_url: string | null;
  avatar_color: string | null;
  years_experience: number | null;
  tenure_months: number | null;
  birthday: string | null;
  gender: "male" | "female" | null;
  location: string | null;
  email: string | null;
  languages: string[];
  expertise: string[];
  certifications: string[];
  experience: ExperienceItem[];
  education: EducationItem[];
  linkedin_url: string | null;
  website_url: string | null;
  is_active: boolean;
  created_at: string;
  projects_count?: number;
  /**
   * When true, this profile is an AI-simulated collaborator. The AI Simulations
   * engine targets only profiles with `special_scheme = true`; real humans are
   * never simulated.
   */
  special_scheme?: boolean;
  /**
   * Resource (price tier) this person inherits from the Resources catalog.
   * When the profile is assigned to a project, the project_team row inherits
   * the resource's monthly_rate as monthly_rate_snapshot.
   */
  resource_id?: string | null;
};

export type ProjectTeamMember = {
  id: string;
  project_id: string;
  team_profile_id: string;
  role_on_project: string | null;
  order_index: number;
  profile: TeamProfile;
};

export type ProjectClientContact = {
  id: string;
  display_name: string;
  role: string | null;
  email: string | null;
  phone: string | null;
  avatar_url: string | null;
  location: string | null;
  is_primary: boolean;
  /** Present when this contact was created via the project invitations flow. */
  invitation_id?: string | null;
  status?: ProjectInvitationStatus | null;
  tab_permissions?: TabPermissions | null;
  invited_at?: string | null;
  accepted_at?: string | null;
  last_session_at?: string | null;
};

export type ProjectClientTeam = {
  projectName: string;
  clientName: string | null;
  contacts: ProjectClientContact[];
};

/* ──────────────────────────────────────────────────────────────────────────
   Project invitations — external people (typically client-side) invited to
   collaborate on a single project with per-tab permissions.
   ────────────────────────────────────────────────────────────────────────── */
export const PROJECT_TABS = [
  "milestones",
  "tasks",
  "meetings",
  "ai",
  "team",
  "documents",
  "credentials",
  "otherDetails",
] as const;
export type ProjectTab = (typeof PROJECT_TABS)[number];
export type TabPermissions = Record<ProjectTab, boolean>;
export const DEFAULT_TAB_PERMISSIONS: TabPermissions = {
  milestones: true,
  tasks: true,
  meetings: true,
  ai: true,
  team: true,
  documents: true,
  credentials: false,
  otherDetails: true,
};
export type ProjectInvitationStatus = "pending" | "active" | "disabled";
export type ProjectInvitation = {
  id: string;
  project_id: string;
  name: string;
  email: string;
  location: string | null;
  is_primary: boolean;
  status: ProjectInvitationStatus;
  tab_permissions: TabPermissions;
  invited_at: string;
  accepted_at: string | null;
  last_session_at: string | null;
};

export type Resource = {
  id: string;
  headline: string;
  /** Spanish translation of the specialty headline (shown when the UI is in Spanish). */
  headline_es: string;
  /** Grouping code (e.g. "software_engineering"). Labels are translated in the UI via i18n. */
  division: string;
  description_en: string;
  description_es: string;
  hourly_rate: number;
  overtime_hourly_rate: number;
  monthly_rate: number;
  monthly_included_hours: number;
  /** Capped at 10 per the product rule. */
  max_overtime_hours: number;
  currency: string;
  is_active: boolean;
  order_index: number;
  created_at: string;
  updated_at?: string;
};

export type AppSettings = {
  /** Global discount applied to all resource rates (0–100). */
  resources_discount_percent: number;
  /** Company / legal info used to populate invoice previews and PDFs. */
  invoice_settings?: InvoiceCompanySettings;
  /** Global defaults for each Project detail tab (Documents, Tasks, Meetings, AI, …). */
  project_tab_settings?: ProjectTabSettings;
  /** Misc settings related to the Quotes module (notifications, etc.). */
  quote_settings?: QuoteSettings;
  /** Central on/off switches for every transactional/notification email
   *  the platform can send. See {@link EmailScenarioId} for the catalog. */
  email_settings?: EmailSettings;
  /** Global behavior for the Admin Studio dashboard and operators' Work Studio. */
  studio_settings?: StudioSettings;
  updated_at?: string;
};

export type StudioSettings = {
  /** Rules for the timer used by operators in the Work Studio. */
  timer?: {
    /** Auto-stop the timer after this many minutes of inactivity. 0 = never. */
    auto_stop_after_min?: number;
    /** Require a task to be selected before starting the timer. */
    require_task_selection?: boolean;
    /** Require a note/description when stopping the timer. */
    require_note_on_stop?: boolean;
    /** Maximum minutes that can be logged in a single entry (cap, 0 = no cap). */
    max_entry_minutes?: number;
    /** Show a confirmation dialog before discarding a running timer. */
    confirm_discard?: boolean;
  };
  /** Visual evidence (screenshots / photos) attached to time entries. */
  evidence?: {
    /** Allow uploading evidence after the timer has been stopped. */
    allow_upload_after_stop?: boolean;
    /** How long (hours) after stopping the timer the operator can still upload evidence. 0 = always. */
    upload_window_hours?: number;
    /** Allow operators to delete the evidence they uploaded. */
    allow_delete_own?: boolean;
    /** Allow admins to delete any evidence. */
    allow_delete_admin?: boolean;
    /** Require at least one piece of evidence per time entry before saving. */
    require_per_entry?: boolean;
    /** Maximum number of evidence files per time entry. */
    max_files_per_entry?: number;
    /** Maximum size (MB) for each evidence file. */
    max_file_size_mb?: number;
  };
  /** Settings for the Admin Studio dashboard widgets. */
  admin_dashboard?: {
    /** Number of recent evidence items to show in the "Recent evidence" widget. */
    recent_evidence_count?: number;
    /** How often (seconds) widgets auto-refresh. 0 = disabled. */
    auto_refresh_seconds?: number;
    /** Show the weekly report widget. */
    show_weekly_report?: boolean;
    /** How many past weeks to include in the weekly report. */
    weekly_report_weeks?: number;
  };
  /** Settings for the operators' Work Studio experience. */
  work_studio?: {
    /** Allow operators to log time on tasks not assigned to them. */
    allow_unassigned_tasks?: boolean;
    /** Allow operators to edit their own past time entries. */
    allow_edit_past_entries?: boolean;
    /** How many days back operators can edit their past entries. 0 = unlimited. */
    edit_window_days?: number;
    /** Allow operators to delete their own time entries. */
    allow_delete_entries?: boolean;
    /** Show daily target hours bar in the Work Studio header. */
    show_daily_target?: boolean;
    /** Daily target hours displayed to operators. */
    daily_target_hours?: number;
  };
};

export type EmailScenarioId =
  | "os-welcome"
  | "os-login-otp"
  | "client-team-invitation"
  | "project-invitation"
  | "project-update"
  | "payment-status-update"
  | "team-change"
  | "invoice-issued"
  | "invoice-reminder"
  | "contact-form-admin"
  | "contact-form-prospect"
  | "ventures-form-admin"
  | "ventures-form-prospect"
  | "quote-sent-to-client"
  | "quote-admin-open"
  | "quote-admin-accept"
  | "quote-admin-no-view-2d"
  | "quote-admin-no-view-4d"
  | "quote-client-reminder-2d"
  | "quote-client-reminder-4d";

export type EmailSettings = {
  scenarios?: Partial<Record<EmailScenarioId, { enabled?: boolean }>>;
};

export type QuoteSettings = {
  /** Email address notified when a client opens / views a quote. */
  open_notification_email?: string;
  /** Show the UI/UX image gallery section in every single-quote view. Defaults to true. */
  uiux_gallery_enabled?: boolean;
  /** Show the Portfolio Projects carousel in every single-quote view. Defaults to true. */
  portfolio_items_enabled?: boolean;
};

export type ProjectTabSettings = {
  documents?: {
    max_file_size_mb?: number;
    allowed_extensions?: string; // comma-separated, e.g. "pdf,docx,jpg"
    default_visible_to_client?: boolean;
    allow_client_upload?: boolean;
    allow_client_create_folders?: boolean;
  };
  tasks?: {
    default_visible_to_client?: boolean;
    allow_client_create?: boolean;
    max_attachment_size_mb?: number;
    default_reminder_cadence?: "off" | "daily" | "weekly";
  };
  meetings?: {
    default_duration_min?: number;
    default_provider?: "google_meet" | "zoom" | "teams" | "in_person" | "other";
    allow_client_create?: boolean;
  };
  ai?: {
    enabled?: boolean;
    default_model?: string;
    max_tokens?: number;
    allow_client_access?: boolean;
  };
  team?: {
    show_team_to_client?: boolean;
    show_contact_info?: boolean;
  };
  milestones?: {
    allow_client_comments?: boolean;
    default_visible_to_client?: boolean;
  };
  credentials?: {
    default_visible_to_client?: boolean;
    require_master_reveal?: boolean;
  };
  pages?: {
    enabled?: boolean;
    default_visible_to_client?: boolean;
    allow_client_edit?: boolean;
    max_pages_per_project?: number;
  };
  activity_log?: {
    enabled?: boolean;
    visible_to_client?: boolean;
    retention_days?: number;
    include_system_events?: boolean;
    notify_client_on_updates?: boolean;
  };
  other_details?: {
    default_visible_to_client?: boolean;
  };
  /** Global AI Simulations governance shared by every project.
   *  Per-project tuning still lives on `projects.ai_settings`; this section
   *  is the master kill-switch + budget guardrail across the platform. */
  ai_simulations_global?: {
    enabled?: boolean;
    kill_switch?: boolean;
    kill_switch_reason?: string;
    kill_switch_activated_at?: string;
    monthly_usd_cap?: number;
    alert_threshold_percent?: number;
    /** Days to retain raw `ai_simulation_events` log rows.
     *  IMPORTANT: only deletes the event/audit log entries, never the
     *  tasks, subtasks, milestones, comments or pages produced by the AI. */
    log_retention_days?: number;
  };
};

export type AISimulationActivityKey =
  | "create_milestones"
  | "create_tasks"
  | "create_subtasks"
  | "assign_tasks"
  | "change_task_status"
  | "comment_tasks"
  | "reply_client_comments"
  | "post_project_updates"
  | "edit_pages";

export type AISimulationsSettings = {
  /** Master switch. Engine still only runs in production. */
  enabled?: boolean;
  /**
   * Emergency kill switch — overrides `enabled`. When true, NO activity
   * is generated for this project, regardless of other settings. Set
   * together with `kill_switch_reason` for auditing.
   */
  kill_switch?: boolean;
  kill_switch_reason?: string;
  kill_switch_activated_at?: string;
  /**
   * Draft mode — all client-facing content (replies, updates, comments)
   * is created as PENDING approval. Admin must approve before publish.
   */
  draft_mode?: boolean;
  /**
   * Dry-run mode — engine simulates and logs events without persisting
   * anything visible. Useful before flipping a project to production.
   * `dry_run_until` is an ISO date when dry-run automatically ends.
   */
  dry_run?: boolean;
  dry_run_until?: string | null;
  /** "all_active" simulates every non-archived project; "selected" uses project_ids. */
  scope?: "all_active" | "selected" | "none";
  project_ids?: string[];
  /** How participants are chosen on each project. */
  participants?: {
    mode?: "all_team" | "sample";
    /** % of team members sampled when mode = "sample" (1–100). */
    sample_percent?: number;
    /** Use only project members whose profile has special_scheme = true. */
    only_special_scheme?: boolean;
    /** Exclude operators currently flagged off-duty. */
    respect_off_duty?: boolean;
    /** Per-person vacation ranges; member won't act in that window. */
    vacations?: Array<{ user_id: string; from: string; to: string }>;
  };
  /**
   * Personality & tone of the simulated team members.
   */
  personality?: {
    tone?: "formal" | "casual" | "technical" | "close";
    /** 0 = no emojis, 3 = lots of emojis. */
    emoji_level?: number;
    /** Output language for generated content. */
    language?: "auto" | "es" | "en";
    /** Preferred length for comments / updates. */
    length?: "short" | "medium" | "long";
  };
  activities?: Partial<Record<AISimulationActivityKey, boolean>>;
  /** Hard sub-limits per activity type, per person, per day. */
  activity_sublimits?: Partial<Record<AISimulationActivityKey, number>>;
  /** Anti-spam cooldown — minimum minutes between similar items. */
  anti_spam_cooldown_minutes?: number;
  schedule?: {
    timezone?: string;
    /** 0 = Sun … 6 = Sat */
    days_of_week?: number[];
    active_hour_start?: number; // 0–23 local
    active_hour_end?: number;   // 0–23 local
    /** 1–10. Higher = more events spread across the day. */
    intensity?: number;
    /** 0–100. Higher = more variation in spacing & content. */
    randomness_percent?: number;
    /** ISO country code for holidays calendar (e.g. "GT", "US", "MX"). */
    holidays_country?: string;
    /** Extra non-working dates added by the admin (ISO YYYY-MM-DD). */
    custom_holidays?: string[];
    /** Days to gradually ramp-up intensity from low to full. */
    ramp_up_days?: number;
    /** Multiplier per weekday (0=Sun..6=Sat). 1 = normal, 0 = off. */
    weekday_intensity?: number[];
  };
  response_delays?: {
    team_min_minutes?: number;
    team_max_minutes?: number;
    client_min_minutes?: number;
    client_max_minutes?: number;
    /** Only respond inside active hours. */
    only_during_active_hours?: boolean;
  };
  /**
   * Content safety filters.
   */
  safety?: {
    /** Lowercased keywords the AI must NOT respond to or use. (legacy) */
    banned_topics?: string[];
    /** When true, never auto-reply on threads flagged sensitive. (legacy) */
    never_reply_sensitive?: boolean;
    /**
     * Dynamic rules engine: topic match → action.
     * - reply: respond following `response_guideline`.
     * - ignore: do not respond.
     * - notify_admin: silently notify an admin instead of responding.
     */
    rules?: Array<{
      id: string;
      topics: string[];
      action: "reply" | "ignore" | "notify_admin";
      response_guideline?: string;
      /** Admin user ids to notify when action = notify_admin. */
      notify_user_ids?: string[];
    }>;
    /** Only run when the project is in one of these stages. */
    allowed_stages?: string[];
  };
  limits?: {
    max_events_per_project_per_day?: number;
    max_events_per_person_per_day?: number;
    max_tokens_per_day?: number;
    model?: string;
  };
  /**
   * Cost & observability.
   */
  budget?: {
    /** Hard USD cap per calendar month for this project. */
    monthly_usd_cap?: number;
    /** Alert recipients when spend reaches this % of the cap (0–100). */
    alert_threshold_percent?: number;
    /** Notify Slack webhook URL when client-facing content is published. */
    slack_webhook_url?: string;
    /** Notify email when client-facing content is published. */
    notification_email?: string;
    /** Admin user IDs that receive budget notifications. */
    notification_user_ids?: string[];
    /** Days to retain raw event logs. */
    log_retention_days?: number;
  };
  daily_report?: {
    enabled?: boolean;
    /** Local hour (0–23) when the summary email is sent. */
    send_hour?: number;
    /** Comma-separated emails (admins) that receive the daily activity digest. Legacy field. */
    recipients?: string;
    /** Selected admin user ids that receive the daily activity digest. */
    recipient_user_ids?: string[];
    /** "es" | "en" */
    language?: "es" | "en";
  };
};

export type InvoiceCompanySettings = {
  commercial_name?: string;
  legal_name?: string;
  tax_id?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  email?: string;
  phone?: string;
  website?: string;
  /** Base64 data URL of the company logo (PNG recommended). */
  logo_data_url?: string;
  bank_name?: string;
  bank_account_holder?: string;
  bank_account_number?: string;
  bank_routing_number?: string;
  bank_swift?: string;
  bank_currency?: string;
  /** @deprecated use payment_instructions_es / _en. Kept for back-compat. */
  payment_instructions?: string;
  /** @deprecated use footer_note_es / _en. Kept for back-compat. */
  footer_note?: string;
  payment_instructions_es?: string;
  payment_instructions_en?: string;
  footer_note_es?: string;
  footer_note_en?: string;
};

/* ────────────────────────────────────────────────────────────────────────────
   Payment methods registry — multiple bank accounts / wallets / payment links.
   ──────────────────────────────────────────────────────────────────────────── */
export type PaymentMethodKind = "bank" | "crypto" | "paypal" | "stripe" | "other";

export type PaymentMethodBankDetails = {
  bank_name?: string;
  account_holder?: string;
  account_number?: string;
  routing_number?: string;
  swift?: string;
  iban?: string;
};
export type PaymentMethodCryptoDetails = {
  network?: string;
  asset?: string;
  wallet_address?: string;
  memo?: string;
};
export type PaymentMethodPaypalDetails = { email?: string; link?: string };
export type PaymentMethodStripeDetails = { payment_link?: string; account_label?: string };
export type PaymentMethodOtherDetails = { text?: string };

export type PaymentMethod = {
  id: string;
  kind: PaymentMethodKind;
  label: string;
  currency: string | null;
  details:
    | PaymentMethodBankDetails
    | PaymentMethodCryptoDetails
    | PaymentMethodPaypalDetails
    | PaymentMethodStripeDetails
    | PaymentMethodOtherDetails
    | Record<string, any>;
  is_active: boolean;
  is_default_global: boolean;
  order_index: number;
  created_at: string;
  updated_at?: string;
  /** Bilingual payment instructions shown on invoices that use this method. */
  payment_instructions_es?: string | null;
  payment_instructions_en?: string | null;
  /** Bilingual invoice footer note shown on invoices that use this method. */
  footer_note_es?: string | null;
  footer_note_en?: string | null;
};

export interface DataAPI {
  // Dashboard
  adminDashboardStats(): Promise<DashboardStats>;

  // Clients
  listClients(): Promise<ClientRow[]>;
  listClientsLite(): Promise<{ id: string; company_name: string; client_type?: "standard" | "special" }[]>;
  getClient(id: string): Promise<{
    client: ClientRow | null;
    projects: Pick<ProjectRow, "id" | "name" | "status" | "progress">[];
    invoices: Pick<Invoice, "id" | "invoice_number" | "amount" | "currency" | "status" | "due_date">[];
  }>;
  createClient(p: Partial<ClientRow>): Promise<string>;
  updateClient(id: string, patch: Partial<ClientRow>): Promise<void>;
  /**
   * Compute the cascading impact of changing a client's active status.
   * Used by the UI to render a confirmation dialog before deactivation /
   * reactivation.
   */
  getClientImpact(id: string): Promise<{
    activeProjects: number;
    openQuotes: number;
    outstandingAmount: number;
    pendingPaymentsCount: number;
    currency: string;
  }>;
  /**
   * Atomically flip a client between "active" and "inactive".
   *  - Deactivate: archive non-archived projects, reject open quotes, pause
   *    auto-billing on every project, and block OS access for any linked
   *    auth user (next session check will sign them out).
   *  - Reactivate: only flips the client back to "active"; projects, quotes
   *    and billing stay where they were so the admin can manually re-enable
   *    each one. New invoices remain paused until billing is turned back on.
   */
  setClientActive(id: string, active: boolean): Promise<void>;

  // Projects
  listProjects(): Promise<ProjectRow[]>;
  createProject(p: Partial<ProjectRow>): Promise<void>;
  /**
   * Create a full project from an accepted quote: copies client, currency,
   * billing rules, seeds the team with locked-in rates, and seeds an initial
   * milestone. Returns the new project id. Throws if the quote is already
   * linked to a project.
   */
  createProjectFromQuote(quoteId: string): Promise<string>;
  getProjectDetail(id: string): Promise<{
    project: ProjectRow | null;
    milestones: Milestone[];
    tasks: Task[];
    members: ProjectMember[];
    updates: ProjectUpdate[];
  }>;
  updateProject(id: string, patch: Partial<ProjectRow>): Promise<void>;
  getProjectAISettings(id: string): Promise<{
    project: Pick<ProjectRow, "id" | "name" | "status" | "ai_settings" | "clients"> | null;
  }>;
  updateProjectAISettings(id: string, settings: AISimulationsSettings): Promise<void>;
  createMilestone(p: {
    project_id: string;
    title: string;
    description?: string;
    start_date?: string | null;
    end_date?: string | null;
    due_date?: string | null;
  }): Promise<void>;
  createTask(p: { project_id: string; title: string; description?: string }): Promise<void>;
  createProjectUpdate(p: { project_id: string; title: string; body?: string }): Promise<void>;
  listOperatorsForAssign(): Promise<OperatorAssignee[]>;
  assignProjectMember(project_id: string, user_id: string): Promise<void>;

  // Invoices
  listInvoices(): Promise<Invoice[]>;
  createInvoice(p: Partial<Invoice>): Promise<string>;
  markInvoicePaid(id: string): Promise<void>;
  updateInvoice(id: string, patch: Partial<Invoice>): Promise<void>;
  deleteInvoice(id: string): Promise<void>;
  listInvoicePayments(invoiceId: string): Promise<Payment[]>;
  updatePayment(id: string, patch: Partial<Payment>): Promise<void>;
  deletePayment(id: string): Promise<void>;
  listInvoiceReminders(invoiceId: string): Promise<InvoiceReminder[]>;
  createInvoiceReminder(p: Partial<InvoiceReminder> & { invoice_id: string }): Promise<void>;

  // Quotes
  listQuotes(): Promise<Quote[]>;
  createQuote(p: Partial<Quote> & { created_by?: string | null }): Promise<string>;
  updateQuote(id: string, patch: Partial<Quote>): Promise<void>;
  deleteQuote(id: string): Promise<void>;
  getQuoteDetail(id: string): Promise<{
    quote: (Quote & { clients?: { company_name: string; contact_name?: string | null; contact_email?: string | null } | null }) | null;
    resources: QuoteResourceRow[];
    team: QuoteTeamRow[];
  }>;

  // Payments
  listPayments(): Promise<Payment[]>;
  createPayment(p: Partial<Payment>): Promise<void>;

  // Users
  listUsersWithRoles(): Promise<UserWithRoles[]>;
  addUserRole(uid: string, role: string): Promise<void>;
  removeUserRole(uid: string, role: string): Promise<void>;

  // Client hub
  clientHubData(userId: string): Promise<{
    projects: Pick<ProjectRow,
      "id" | "name" | "status" | "progress" |
      "due_date" | "start_date" | "created_at" | "currency" | "budget" | "client_id"
    >[];
    invoices: Invoice[];
  }>;
  clientInvoices(userId: string): Promise<Invoice[]>;
  clientProjectDetail(id: string): Promise<{
    project: ProjectRow | null;
    milestones: Milestone[];
    tasks: Task[];
    updates: ProjectUpdate[];
    quoteSummary?: string | null;
  }>;

  // Operator hub
  operatorAssignedProjects(userId: string): Promise<ProjectRow[]>;

  // Insights & alerts (admin only)
  adminInsights(): Promise<AdminInsights>;

  // Team profiles (Admin Hub) & project assignments (visible to client)
  listTeamProfiles(): Promise<TeamProfile[]>;
  getTeamProfile(id: string): Promise<TeamProfile | null>;
  createTeamProfile(p: Partial<TeamProfile>): Promise<string>;
  updateTeamProfile(id: string, patch: Partial<TeamProfile>): Promise<void>;
  deleteTeamProfile(id: string): Promise<void>;
  listProjectTeam(projectId: string): Promise<ProjectTeamMember[]>;
  assignTeamToProject(projectId: string, teamProfileId: string, roleOnProject?: string): Promise<void>;
  removeTeamFromProject(projectTeamId: string): Promise<void>;
  listProjectClientTeam(projectId: string): Promise<ProjectClientTeam>;

  // Seeded client contacts overrides (mock — primary contact pulled from clients table)
  updateClientContact(
    contactId: string,
    patch: Partial<Pick<ProjectClientContact, "display_name" | "email" | "location" | "is_primary" | "tab_permissions" | "status">>,
  ): Promise<void>;
  deleteClientContact(contactId: string): Promise<void>;

  // Project invitations (client-side collaborators)
  listProjectInvitations(projectId: string): Promise<ProjectInvitation[]>;
  createProjectInvitation(
    projectId: string,
    p: Pick<ProjectInvitation, "name" | "email"> &
      Partial<Pick<ProjectInvitation, "location" | "is_primary" | "tab_permissions">>,
  ): Promise<string>;
  updateProjectInvitation(
    id: string,
    patch: Partial<Pick<ProjectInvitation, "name" | "email" | "location" | "is_primary" | "tab_permissions" | "status">>,
  ): Promise<void>;
  deleteProjectInvitation(id: string): Promise<void>;

  // Resources catalog (Admin Hub)
  listResources(): Promise<Resource[]>;
  createResource(p: Partial<Resource>): Promise<string>;
  updateResource(id: string, patch: Partial<Resource>): Promise<void>;
  deleteResource(id: string): Promise<void>;

  // Global app settings (Admin Hub)
  getAppSettings(): Promise<AppSettings>;
  updateAppSettings(patch: Partial<AppSettings>): Promise<void>;

  // Payment methods registry
  listPaymentMethods(): Promise<PaymentMethod[]>;
  createPaymentMethod(p: Partial<PaymentMethod>): Promise<string>;
  updatePaymentMethod(id: string, patch: Partial<PaymentMethod>): Promise<void>;
  deletePaymentMethod(id: string): Promise<void>;
}